import React, { createContext, useContext, useEffect, useState } from "react";
import * as signalR from "@microsoft/signalr";
import { baseurl } from "../../authConfig";
import { useAccessToken } from "./AccessTokenContextV2";

interface NotificationContextProps {
  connectionTarget: string;
  setConnectionTarget: (target: string) => void;
  setReturnedReportId: (target: string) => void;
  setIsReportCompleted: (target: boolean) => void;
  notifications: any[] | null;
  returnedReportId: any;
  isReportCompleted: boolean;
  connection: signalR.HubConnection | null;
  progress: number | undefined;
  setProgress: (target: number) => void;
  isDVCRunComplete: boolean
  snackbarMessage: string | null;
  setSnackbarMessage: (msg: string | null) => void;
  snackbarType: any
  moduleAwaitingAuditResult: string,
  setModuleAwaitingResult: (target: string) => void;
  targetTabValue: number | null,
  setTargetTabValue: (target: number) => void,
  setSnackbarType: Function,
  setIsFromDataAudit: Function,
  setIsFromGMP: Function
  runInstanceIdGMP: number | null,
  runInstanceIdDataAudit: number | null,
  isFromGMP: boolean,
  isFromDataAudit: boolean,
  runProgressStatus: string,
  setRunProgressStatus: (target: string) => void;
}

function useLocalStorageState<T>(key: string, defaultValue: T) {
  const [state, setState] = React.useState<T>(() => {
    const stored = localStorage.getItem(key);
    return stored ? (JSON.parse(stored) as T) : defaultValue;
  });

  React.useEffect(() => {
    localStorage.setItem(key, JSON.stringify(state));
  }, [key, state]);

  return [state, setState] as const;
}

const NotificationContext = createContext<NotificationContextProps>( {} as NotificationContextProps);

export const useNotificationContext = () => useContext(NotificationContext);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [connectionTarget, setConnectionTarget] = useLocalStorageState<string>("connectionTarget", "");
  const [notifications, setNotifications] = useState<any[] | null>(null);
  const [returnedReportId, setReturnedReportId] = useState<string | null>(null);
  const [isReportCompleted, setIsReportCompleted] = useState<boolean>(false);
  const [isDVCRunComplete, setIsDVCRunComplete] = useState<boolean>(false);
  const [connection, setConnection] = useState<signalR.HubConnection | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [snackbarType, setSnackbarType] = useState<any>(null);
  const [moduleAwaitingAuditResult, setModuleAwaitingResult] = useState<string>("");
  const [targetTabValue, setTargetTabValue] = useState<number>(0);
  const [runInstanceIdDataAudit, setRunInstanceIdDataAudit] = useState<number | null>(null);
  const [runInstanceIdGMP, setRunInstanceIdGMP] = useState<number | null>(null);
  const [isFromDataAudit, setIsFromDataAudit] = useLocalStorageState<boolean>("isFromDataAudit", false);
  const [runProgressStatus, setRunProgressStatus] = useState<string>("");
  const [isFromGMP, setIsFromGMP] = useState<boolean>(false);
  const { accessToken } = useAccessToken();

  useEffect(() => {
  if (!accessToken) return;

  const newConnection = new signalR.HubConnectionBuilder()
    .withUrl(`${baseurl}/notificationHub`, {
      accessTokenFactory: async () => accessToken ?? "",
    })
    .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
    .build();

  newConnection.serverTimeoutInMilliseconds = 120000; 
  newConnection.keepAliveIntervalInMilliseconds = 15000; 

  newConnection
    .start()
    .then()
    .catch((err: any) => (err));

  newConnection.onreconnecting(() => console.warn());
  newConnection.onreconnected(() => console.log());
  newConnection.onclose((err: any) => (err));

  setConnection(newConnection);

  return () => {
    newConnection.stop().then();
  };
}, [accessToken]);


useEffect(() => {
  if (!connection || !connectionTarget) return;

  const handler = async (data: any) => {
    setNotifications((prev) => [...(prev || []), data]);

    if (connectionTarget === "DVCRunRequest" || connectionTarget === "ScopingCheck") {
      setProgress(data.progress);
      setRunProgressStatus(data.runStatus);

      if (data.runStatus === "S" && data.reportId === null) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }

      if (data.runStatus === "S" && data.reportId !== null) {
        setIsDVCRunComplete(true);
        setSnackbarMessage("DVC Run complete");
        setSnackbarType("success");
        setProgress(0);
        setRunInstanceIdDataAudit(null);
        setRunInstanceIdGMP(null);
        setRunProgressStatus(data.runStatus);
        setReturnedReportId(data.reportId);
        setIsReportCompleted(true);
      }

      if (data.runStatus === "P" && data.reportId === null) {
        isFromDataAudit
          ? setRunInstanceIdDataAudit(data.runInstanceId)
          : isFromGMP
          ? setRunInstanceIdGMP(data.runInstanceId)
          : null;
        setRunProgressStatus(data.runStatus);
      }

      if (data.runStatus === "E" && data.reportId === null) {
        setIsDVCRunComplete(false);
        setSnackbarMessage("DVC Run failed");
        setSnackbarType("error");
        setConnectionTarget("Connection");
        setProgress(0);
        setRunProgressStatus(data.runStatus);
      }
    } else if (connectionTarget === "GmpEReportReady") {
      setReturnedReportId(data);
      setIsReportCompleted(true);
      setSnackbarType("success");
      setSnackbarMessage("Your report is ready to download");
      setRunProgressStatus("");
    }
  };

  connection.on(connectionTarget, handler);

  return () => {
    connection.off(connectionTarget, handler);
  };
}, [connection, connectionTarget]);



  return (
    <NotificationContext.Provider
      value={{
        connectionTarget,
        setConnectionTarget,
        setReturnedReportId,
        setIsReportCompleted,
        setProgress,
        notifications,
        returnedReportId,
        isReportCompleted,
        connection,
        progress,
        isDVCRunComplete,
        snackbarMessage,
        setSnackbarMessage,
        snackbarType,
        moduleAwaitingAuditResult,
        setModuleAwaitingResult,
        targetTabValue, 
        setTargetTabValue,
        setSnackbarType,
        setIsFromDataAudit,
        setIsFromGMP,
        runInstanceIdDataAudit,
        runInstanceIdGMP,
        isFromGMP,
        isFromDataAudit,
        runProgressStatus,
        setRunProgressStatus
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
